import { isFolderLike, type FileSystemItem } from './types';

export const getParentPath = (path: string): string => {
  const segments = path.split('/').filter(Boolean);
  segments.pop();
  return segments.length ? `/${segments.join('/')}` : '/';
};

/**
 * The path's own name — its last segment. Empty string at the root, which has no name of its own;
 * callers that need to label the root have to decide what to say about it rather than printing "".
 */
export const getPathName = (path: string): string =>
  path.split('/').filter(Boolean).pop() ?? '';

export const isAtRoot = (path: string): boolean => path === '/';

export const isDescendantPath = (
  path: string,
  ancestorPath: string,
): boolean => {
  if (path === ancestorPath) return false;
  if (ancestorPath === '/') return true;
  return path.startsWith(`${ancestorPath}/`);
};

/**
 * Whether `path` is `candidateAncestorPath` itself, or nested somewhere underneath it. Extracted
 * from the condition useMoveItemMutation's onSuccess originally inlined
 * (`currentPath.value === item.path || currentPath.value.startsWith(`${item.path}/`)`), generalized
 * to take both paths as plain params instead of reading refs — both move's "did I move the current
 * folder (or an ancestor of it)?" check and delete's "navigate up if the deleted item was the
 * current folder or an ancestor of it" check need exactly this condition, so it's shared rather than
 * re-derived. Deliberately not the same shape as isDescendantPath() above: this one includes the
 * self case (path === candidateAncestorPath) and has no root ("/") special-case, since a path can
 * always equal or be nested under a non-root candidate the same way regardless of where that
 * candidate sits.
 */
export const isAncestorOrSelf = (
  candidateAncestorPath: string,
  path: string,
): boolean =>
  path === candidateAncestorPath ||
  path.startsWith(`${candidateAncestorPath}/`);

export const normalizeVirtualPath = (
  raw: string | null | undefined | (string | null)[],
): string => {
  if (typeof raw !== 'string') return '/';
  const segments = raw.split('/').filter(Boolean);
  return segments.length ? `/${segments.join('/')}` : '/';
};

export type ItemBreakdown = {
  fileCount: number;
  directoryCount: number;
  total: number;
};

/**
 * How many files vs folders a resolved selection contains — the shape every modal that has to
 * describe a batch of items in plain language builds its wording from.
 *
 * Verb-neutral on purpose: it started life as describeDeleteTarget inside useFileManagerDelete and
 * moved here once the transfer confirmation needed exactly the same counts. It counts, and says
 * nothing about what is about to happen to them. Mounts count as folders — they can never be part of
 * a delete or transfer target (never drag sources, never selectable as such), but narrowing by
 * `type === 'directory'` alone would silently miscount if that ever changed.
 */
export const describeItems = (items: FileSystemItem[]): ItemBreakdown => {
  const directoryCount = items.filter((item) => isFolderLike(item)).length;
  return {
    directoryCount,
    fileCount: items.length - directoryCount,
    total: items.length,
  };
};
